"use client";

import { useQuery } from "@tanstack/react-query";

import React, { useEffect, useMemo, useRef } from "react";
import { FiX } from "react-icons/fi";

import Badge from "@/components/ui/badge";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { cx } from "@/lib/tailwind-utils";

import type { GFEClearance } from "@/types/forms/question";
import {
  type GFEClearanceRow,
  type GFEClearanceValue,
  GFEItemOutcome,
} from "@/types/gfe";
import type { NoteAnswerDetail } from "@/types/notes";

import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { gfeQueryOptions } from "@/query/gfe";

const DEFAULT_VALIDITY_DAYS = 365;

const OUTCOMES: { value: GFEItemOutcome; label: string }[] = [
  { value: GFEItemOutcome.APPROVED, label: "Approved" },
  { value: GFEItemOutcome.CONDITIONAL, label: "Conditional" },
  { value: GFEItemOutcome.DEFERRED, label: "Deferred" },
];

// The picked outcome takes its status colour, as the signed view's badge does, so a
// deferral stands out in a column of decisions before the provider signs.
const SELECTED_OUTCOME_CLASS: Partial<Record<GFEItemOutcome, string>> = {
  [GFEItemOutcome.APPROVED]:
    "border-transparent bg-status-success-bg text-status-success-text hover:bg-status-success-bg md:hover:bg-status-success-bg",
  [GFEItemOutcome.CONDITIONAL]:
    "border-transparent bg-status-warning-bg text-status-warning-text hover:bg-status-warning-bg md:hover:bg-status-warning-bg",
  [GFEItemOutcome.DEFERRED]:
    "border-transparent bg-status-error-bg text-status-error-text hover:bg-status-error-bg md:hover:bg-status-error-bg",
};

const OUTCOME_BADGE: Record<
  GFEItemOutcome,
  { label: string; variant: "success" | "warning" | "error" | "neutral" }
> = {
  [GFEItemOutcome.APPROVED]: { label: "Approved", variant: "success" },
  [GFEItemOutcome.CONDITIONAL]: { label: "Conditional", variant: "warning" },
  [GFEItemOutcome.DEFERRED]: { label: "Deferred", variant: "error" },
  [GFEItemOutcome.PENDING]: { label: "Not decided", variant: "neutral" },
};

function readValue(data: NoteAnswerDetail["data"]): GFEClearanceValue {
  const raw = (data as { value?: unknown })?.value;
  if (typeof raw !== "object" || raw === null) return { rows: [] };
  const value = raw as Partial<GFEClearanceValue>;
  const rows = Array.isArray(value.rows)
    ? value.rows.flatMap((row) => {
        if (typeof row !== "object" || row === null) return [];
        const { treatmentId, outcome, conditionNote, treatmentName } =
          row as Partial<GFEClearanceRow>;
        if (typeof treatmentId !== "string" || !treatmentId) return [];
        return [
          {
            treatmentId,
            outcome: Object.values(GFEItemOutcome).includes(
              outcome as GFEItemOutcome,
            )
              ? (outcome as GFEItemOutcome)
              : GFEItemOutcome.PENDING,
            conditionNote:
              typeof conditionNote === "string" ? conditionNote : null,
            treatmentName:
              typeof treatmentName === "string" ? treatmentName : undefined,
          },
        ];
      })
    : [];
  return {
    rows,
    ...(typeof value.validityDays === "number" && {
      validityDays: value.validityDays,
    }),
  };
}

interface GFEClearanceAnswerEditorProps {
  answer: NoteAnswerDetail;
  onChange: (answerId: string, data: Record<string, unknown>) => void;
  disabled?: boolean;
  title?: string;
  questionData?: GFEClearance;
  /** The note this answer belongs to; its appointment pre-fills the rows. */
  noteId?: string;
}

/**
 * The provider's in-house Good Faith Exam decision: each treatment approved,
 * conditional or deferred, and how long the clearance lasts. Signing the note
 * turns it into a GFE that covers the treatments it cleared.
 */
export const GFEClearanceAnswerEditor: React.FC<
  GFEClearanceAnswerEditorProps
> = ({ answer, onChange, disabled = false, title, questionData, noteId }) => {
  const value = readValue(answer.data);
  // Some payloads carry the question's settings in snake_case.
  const templateData = questionData as
    | (GFEClearance & { default_validity_days?: number })
    | undefined;
  const defaultValidity =
    templateData?.defaultValidityDays ??
    templateData?.default_validity_days ??
    DEFAULT_VALIDITY_DAYS;
  const validityDays = value.validityDays ?? defaultValidity;

  // Archived treatments included so a GFE that cleared one still reads by name.
  // Only while editing: read-only views (the patient portal among them) use the
  // name stored on each row and can't load the clinic's list.
  const { data: treatments, isLoading } = useQuery({
    ...gfeQueryOptions.treatments(true),
    enabled: !disabled,
  });
  const namesById = useMemo(
    () => new Map((treatments ?? []).map((t) => [t.id, t.name])),
    [treatments],
  );
  const addable = (treatments ?? []).filter(
    (t) => !t.isArchived && !value.rows.some((row) => row.treatmentId === t.id),
  );

  const { data: note } = useQuery({
    ...noteQueryOptions.detail(noteId ?? ""),
    enabled: !!noteId && !disabled,
  });
  const eventId = note?.eventId ?? null;
  const { data: context } = useQuery({
    ...gfeQueryOptions.inHouseContext(eventId),
    enabled: !disabled && !!eventId && value.rows.length === 0,
  });

  const update = (next: Partial<GFEClearanceValue>) => {
    // validityDays is saved only once the provider sets it; until then the server
    // applies the template's default, so a stale question payload can't stamp one.
    const merged = { ...value, ...next };
    onChange(answer.id, {
      ...answer.data,
      value: {
        ...merged,
        // Keep each row's name with it: the patient portal can't load the list.
        rows: merged.rows.map((row) => ({
          ...row,
          treatmentName: namesById.get(row.treatmentId) ?? row.treatmentName,
        })),
      },
    });
  };

  // Start an empty clearance with the treatments this appointment's services
  // need, undecided, so the provider only has to mark each one.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || disabled || value.rows.length > 0) return;
    const ids = context?.treatmentIds ?? [];
    if (ids.length === 0) return;
    prefilled.current = true;
    update({
      rows: ids.map((treatmentId) => ({
        treatmentId,
        outcome: GFEItemOutcome.PENDING,
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, disabled]);

  const setRow = (treatmentId: string, changes: Partial<GFEClearanceRow>) =>
    update({
      rows: value.rows.map((row) =>
        row.treatmentId === treatmentId ? { ...row, ...changes } : row,
      ),
    });

  return (
    <div className="space-y-3">
      {title && <Label className="text-body-strong">{title}</Label>}

      {isLoading && <Skeleton className="h-10 w-full rounded-md" />}

      {!isLoading && value.rows.length === 0 && (
        <p className="text-caption text-foreground-muted">
          {disabled
            ? "—"
            : "Add each treatment you evaluated and mark it approved, conditional or deferred."}
        </p>
      )}

      {!isLoading && value.rows.length > 0 && (
        <ul className="divide-y divide-border">
          {value.rows.map((row) => {
            const name =
              namesById.get(row.treatmentId) ??
              row.treatmentName ??
              row.treatmentId;
            const badge = OUTCOME_BADGE[row.outcome];
            const needsNote =
              row.outcome === GFEItemOutcome.CONDITIONAL ||
              row.outcome === GFEItemOutcome.DEFERRED;
            return (
              <li key={row.treatmentId} className="space-y-2 py-2.5">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <p className="min-w-0 grow basis-40 truncate text-body-strong text-foreground">
                    {name}
                  </p>
                  {disabled ? (
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  ) : (
                    <div className="ml-auto flex shrink-0 items-center gap-1">
                      {OUTCOMES.map((option) => (
                        <Button
                          key={option.value}
                          size="sm"
                          variant="secondary"
                          className={
                            row.outcome === option.value
                              ? SELECTED_OUTCOME_CLASS[option.value]
                              : undefined
                          }
                          aria-pressed={row.outcome === option.value}
                          onClick={() =>
                            setRow(row.treatmentId, {
                              outcome: option.value,
                              // A condition or reason belongs to the old decision.
                              ...(row.outcome !== option.value && {
                                conditionNote: null,
                              }),
                            })
                          }
                        >
                          {option.label}
                        </Button>
                      ))}
                      {/* Set apart from the outcomes: a slip here drops the row and its note. */}
                      <span aria-hidden className="mx-2 h-5 w-px bg-border" />
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove ${name}`}
                        onClick={() =>
                          update({
                            rows: value.rows.filter(
                              (r) => r.treatmentId !== row.treatmentId,
                            ),
                          })
                        }
                      >
                        <FiX className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {needsNote &&
                  (disabled ? (
                    row.conditionNote && (
                      <p className="text-caption text-foreground-secondary">
                        {row.conditionNote}
                      </p>
                    )
                  ) : (
                    <Input
                      value={row.conditionNote ?? ""}
                      placeholder={
                        row.outcome === GFEItemOutcome.CONDITIONAL
                          ? "Conditions, e.g. max 2 syringes"
                          : "Reason for deferring"
                      }
                      onChange={(e) =>
                        setRow(row.treatmentId, {
                          conditionNote: e.target.value,
                        })
                      }
                    />
                  ))}
              </li>
            );
          })}
        </ul>
      )}

      {!disabled && addable.length > 0 && (
        <Select
          value=""
          onValueChange={(treatmentId) =>
            update({
              rows: [
                ...value.rows,
                { treatmentId, outcome: GFEItemOutcome.PENDING },
              ],
            })
          }
        >
          <SelectTrigger className="sm:w-72">
            <SelectValue placeholder="Add a treatment" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {addable.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}

      <div
        className={cx(
          "flex flex-wrap items-center gap-2",
          disabled && "text-foreground-secondary",
        )}
      >
        <span className="text-body">Valid for</span>
        {disabled ? (
          <span className="text-body-strong">{validityDays}</span>
        ) : (
          <NumberInput
            className="w-24"
            min={1}
            max={3650}
            value={validityDays}
            onValueChange={(days) => update({ validityDays: days })}
          />
        )}
        <span className="text-body">days from sign-off</span>
      </div>
    </div>
  );
};
